import type { ListBuilderConfiguration, ListBuilderList } from '../types';
import { LICENSE_STATUS_STYLE_MAP } from './license/status-style-map';

/**
 * Client Licenses — the list embedded on the client detail page's Licenses tab.
 *
 * This is a STANDALONE config, intentionally NOT derived from the compliance
 * "All Licenses" list. The two surfaces are different products:
 *
 *   - The compliance/licensing-management list overlays `workd_mission_control`
 *     registry data (registrant, registry expiration, ERP-vs-registry
 *     discrepancies, verification file counts). That's the paid licensing
 *     product — gated, and with no production data yet.
 *   - This client tab is a general-CRM surface every tenant sees. It shows ONLY
 *     the tenant's own `client_license` data (served by the tenant-only
 *     ClientsService.getClientLicenses — no mission_control access).
 *
 * Columns are limited to the fields that actually carry tenant data in
 * production (verified across ~180k prod licenses): License # + type, Expires,
 * and Status. Registry-derived columns (Registrant, Category), the Files column
 * (registry verification proofs / no tenant attachments in prod yet), the
 * redundant Client column, and the compliance-only Review Match action are all
 * omitted. They return here behind the compliance gate when the licensing
 * product ships.
 *
 * Filters are SHARED with the compliance list (defined once in
 * libs/core/src/filters/config/license.ts and surfaced per-list via each
 * filter's `listKeys`) — the underlying columns/query semantics are identical,
 * so there's no reason to duplicate them. We just list the data-bearing keys in
 * `activeFilters` below.
 */

const clientLicensesList: ListBuilderList = {
	id: 'default-client-licenses',
	cacheTTL: 600000,
	name: 'Client Licenses',
	type: 'default',
	active: true,
	sortOrder: 0,
	columns: [
		{
			id: 'license-status',
			fields: [
				{
					id: 'status-field',
					dataConfig: {
						type: 'field',
						field: {
							// Stored `client_license.status` is the source of truth.
							// The daily mark-expired job mirrors expiration into
							// status, so the badge stays accurate over time.
							fieldName: 'status',
							objectTable: 'client_license',
						},
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'sm',
						colorRule: {
							type: 'status_map',
							statusMap: { ...LICENSE_STATUS_STYLE_MAP },
						},
					},
				},
				{
					// Disciplinary pill — folded into the Status cell so we don't
					// spend a column on a flag that's clean for ~every license.
					// Only appears when `hasDisciplinaryAction` is true.
					id: 'disciplinary-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Disciplinary',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'danger' },
						},
					},
					conditionalVisibility: {
						field: 'hasDisciplinaryAction',
						condition: 'equals',
						value: true,
					},
				},
				{
					// Link Ended pill (2026-09-05) — the state board no longer links
					// this confirmed licence to another licence the client holds
					// (the vet left the premise, the PIC changed). The match is
					// kept; a human decides. Gated on `linkEndedAt`, hydrated by
					// ClientsService.getClientLicenses from the confirmed match's
					// match_signals (written by the nightly link-drift stage).
					id: 'link-ended-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Link Ended',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'warning' },
						},
					},
					conditionalVisibility: {
						field: 'linkEndedAt',
						condition: 'is_not_null',
					},
				},
				{
					// Matched pill (PD-13978) — stacked under the status badge so
					// the rep can tell at a glance which licenses are linked to a
					// registry record. Gated on `fkRegistryLicenseId` (the
					// confirmed-match FK, hydrated onto every row by
					// ClientsService.getClientLicenses), so it shows only for
					// matched licenses. Theme-aware "green" (resolves to the right
					// shade in light/dark) rather than a raw hex, to align with the
					// success-color usage elsewhere.
					id: 'matched-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Matched',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'success' },
						},
					},
					conditionalVisibility: {
						field: 'fkRegistryLicenseId',
						condition: 'is_not_null',
					},
				},
				{
					// Suggested-match pill (PD-14397) — the row-level affordance
					// that a matchmaker suggestion is waiting on THIS license.
					// Before this pill, a pending suggestion was only visible via
					// the tab-level banner / overflow action, and the row itself
					// looked like any unmatched license (its dropdown collapsed
					// to Edit + Delete). Gated on `pendingMatchId` (hydrated by
					// ClientsService.getClientLicenses from the license's
					// pending_review match); confirmed licenses have their
					// pending rows superseded at confirm time, so this never
					// overlaps the Matched pill above.
					id: 'suggested-match-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Suggested match',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'info' },
						},
					},
					conditionalVisibility: {
						field: 'pendingMatchId',
						condition: 'is_not_null',
					},
				},
				{
					// PD-14845: "Primary" pill — marks the licence the ERP
					// integration and order validation act on when a client
					// holds 2+ of the same type at one address. Reads the
					// hydrated `isPrimary` flag.
					id: 'primary-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Primary',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'info' },
						},
					},
					conditionalVisibility: {
						field: 'isPrimary',
						condition: 'equals',
						value: true,
					},
				},
				{
					// PD-17159: the row is flagged primary but is not in force,
					// and an in-force license of its type at the same address is
					// used instead (resolvePrimaryLicense step 0). Hydrated by
					// ClientLicensesTab as `isPrimaryOverridden`.
					id: 'primary-overridden-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Primary overridden',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'warning' },
						},
					},
					conditionalVisibility: {
						field: 'isPrimaryOverridden',
						condition: 'equals',
						value: true,
					},
				},
			],
			label: 'Status',
			headers: [{ id: 'h-status', text: 'Status' }],
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			sortable: true,
			sortFields: [
				{
					direction: 'ASC',
					fieldName: 'status',
					objectTable: 'client_license',
				},
			],
			width: 96,
		},
		{
			id: 'license-number-type',
			fields: [
				{
					id: 'license-number-field',
					link: {
						type: 'object',
						enabled: true,
						objectTable: 'client_license',
					},
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'licenseNumber',
							objectTable: 'client_license',
						},
					},
					styleConfig: {
						size: 'md',
						weight: 'medium',
						colorRule: { type: 'default' },
					},
					displayStyle: 'text',
				},
				{
					id: 'line-break-1',
					dataConfig: { type: 'custom_text', customText: '' },
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					displayStyle: 'line_break',
				},
				{
					// `typeLabel` is projected by ClientsService.getClientLicenses
					// from the local `client_license.type` (e.g. STATE_BOARD →
					// "State Board") so the user never sees the raw storage form.
					id: 'type-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'typeLabel',
							objectTable: 'client_license',
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: {
							type: 'theme',
							themeColor: 'textSecondary',
						},
					},
					displayStyle: 'text',
				},
				{
					// PD-17113: the DEA on this row is registered at a different
					// address than any of the account's locations. The pill sits
					// on the DEA number itself; the tab callout above the list
					// shows the registered address against the ship-to. Gated on
					// `deaLocationVerdict`, hydrated by
					// ClientsService.getClientLicenses from the same set the
					// Compliance dashboard lists.
					id: 'dea-different-address-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Different address',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'warning' },
						},
					},
					conditionalVisibility: {
						field: 'deaLocationVerdict',
						condition: 'equals',
						value: 'DIFFERENT',
					},
				},
				{
					// PD-17113: the address check could not tell whether this DEA
					// is registered at one of the account's locations (a
					// neighboring house number, a PO box, a street written another
					// way) — shown so a person can look.
					id: 'dea-address-may-differ-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Address may differ',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'warning' },
						},
					},
					conditionalVisibility: {
						field: 'deaLocationVerdict',
						condition: 'equals',
						value: 'UNSURE',
					},
				},
			],
			label: 'License #',
			headers: [{ id: 'h-number', text: 'License #' }],
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			sortable: true,
			sortFields: [
				{
					direction: 'ASC',
					fieldName: 'licenseNumber',
					objectTable: 'client_license',
				},
			],
			width: 144,
		},
		{
			id: 'license-expiration',
			fields: [
				{
					id: 'expiration-field',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'expirationDate',
							objectTable: 'client_license',
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: { type: 'default' },
						formatOptions: { dateFormat: 'mdy' },
					},
					displayStyle: 'date',
				},
				{
					// PD-13411: amber "Expiring Soon" pill under the date —
					// same rule + rendering as the compliance Current Licenses
					// list (isExpiringSoon from the shared isLicenseExpiringSoon
					// helper) so the client tab and the compliance list agree.
					id: 'expiring-soon-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Expiring Soon',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'warning' },
						},
					},
					conditionalVisibility: {
						field: 'isExpiringSoon',
						condition: 'equals',
						value: true,
					},
				},
				{
					// PD-13411: red "Expired" pill once the expiration day has
					// passed — same rule + rendering as the compliance Current
					// Licenses list so the client tab and compliance list agree.
					id: 'expired-pill',
					dataConfig: {
						type: 'custom_text',
						customText: 'Expired',
					},
					displayStyle: 'badge',
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'static',
							staticColor: { type: 'danger' },
						},
					},
					conditionalVisibility: {
						field: 'isExpired',
						condition: 'equals',
						value: true,
					},
				},
			],
			label: 'Expires',
			headers: [{ id: 'h-expiration', text: 'Expires' }],
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			sortable: true,
			sortFields: [
				{
					direction: 'ASC',
					fieldName: 'expirationDate',
					objectTable: 'client_license',
				},
			],
			width: 112,
		},
		{
			id: 'license-actions',
			fields: [
				{
					id: 'actions-dropdown',
					dataConfig: { type: 'custom_text', customText: '' },
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
					},
					displayStyle: 'actions_dropdown',
					dropdownActions: [
						{
							// Review a CONFIRMED registry match (PD-13979). For a
							// matched license, the magnifying-glass "Search" action is
							// wrong — there's nothing to search, the license is already
							// linked. This eye action opens the LicenseReviewModal (which
							// shows the client's confirmed matches + verification
							// proof), letting the rep review/unmatch instead. Gated on
							// the confirmed-match FK, so it's mutually exclusive with the
							// "Search for matches" action below (a row shows one or the
							// other, never both).
							id: 'review-matched-action',
							label: 'Review match',
							iconName: 'eye',
							callbackName: 'reviewMatched',
							variant: 'primary',
							visibility: 'visible',
							requiredPermission: 'clients:licenses:update',
							conditionalVisibility: {
								field: 'fkRegistryLicenseId',
								condition: 'is_not_null',
							},
						},
						{
							// Review a PENDING suggested match (PD-14397). A license with
							// a matchmaker suggestion waiting used to show NO row-level
							// way into the review flow (its dropdown collapsed to Edit +
							// Delete; only the tab banner hinted at the suggestion).
							// Pairs with the "Suggested match" pill in the Status cell
							// and opens the same LicenseReviewModal the banner's Manage
							// matches button opens. Gated on the pending-suggestion id,
							// so it's mutually exclusive with "Review match" above
							// (confirmed) and "Match" below (nothing found yet).
							id: 'review-suggested-action',
							label: 'Review suggested match',
							iconName: 'eye',
							callbackName: 'reviewSuggested',
							variant: 'primary',
							visibility: 'visible',
							requiredPermission: 'clients:licenses:update',
							conditionalVisibility: {
								field: 'pendingMatchId',
								condition: 'is_not_null',
							},
						},
						{
							// Manual registry match search (PD-13915). Shown ONLY on
							// licenses that are neither matched nor have a pending
							// suggestion — i.e. genuinely unmatched (PD-13979). Once the
							// matchmaker surfaces a suggestion the rep reviews it via
							// the "Review suggested match" action above (PD-14397), and
							// once a match is CONFIRMED the "Review match" eye action
							// takes over. The compound "unmatched AND no pending"
							// predicate can't be expressed by the single-field
							// conditionalVisibility, so it flows through the
							// `canSearchForMatches` visibility callback.
							id: 'search-for-matches-action',
							// "Match" — same label as the licenses lists' action and
							// the Matching page's Pending Manual Match tab (PD-13998).
							label: 'Match',
							iconName: 'magnifying-glass',
							callbackName: 'searchForMatches',
							variant: 'secondary',
							visibility: 'visible',
							requiredPermission: 'clients:licenses:update',
							visibilityCallback: 'canSearchForMatches',
						},
						{
							// PD-14845: designate this licence the primary for its
							// (address, type). Shown only when the client holds 2+ of
							// this type at the address and this row isn't already the
							// primary — the `canMakePrimary` callback expresses that
							// compound predicate.
							id: 'make-primary-action',
							label: 'Make primary',
							iconName: 'star',
							callbackName: 'makePrimary',
							variant: 'secondary',
							visibility: 'visible',
							requiredPermission: 'clients:licenses:update',
							visibilityCallback: 'canMakePrimary',
						},
						{
							id: 'edit-action',
							label: 'Edit',
							iconName: 'pen',
							callbackName: 'edit',
							variant: 'secondary',
							visibility: 'visible',
							requiredPermission: 'clients:licenses:update',
						},
						{
							id: 'delete-action',
							label: 'Delete',
							iconName: 'trash',
							callbackName: 'delete',
							variant: 'danger',
							visibility: 'visible',
							requiredPermission: 'clients:licenses:delete',
						},
					],
				},
			],
			label: '',
			headers: [{ id: 'h-actions', text: '' }],
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			sortable: false,
			width: 64,
		},
	],
	defaultSortId: 'column-license-expiration-asc',
	clickAction: {
		type: 'navigate',
		path: '/licenses/{id}',
	},
	actions: [],
};

export const CLIENT_LICENSES_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [clientLicensesList],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		// Shared license filters (license.ts), limited to the keys that carry
		// tenant data in production. licenseState + licenseHasDisciplinary are
		// dropped — both are empty across all prod tenants.
		activeFilters: [
			'licenseKeywords',
			'licenseStatus',
			'licenseType',
			'licenseExpirationDateRange',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/licenses/:id',
			objectLabel: 'License',
			objectTable: 'client_license',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'licenseNumber', fieldLabel: 'License #' },
				{ fieldName: 'type', fieldLabel: 'Type' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'expirationDate', fieldLabel: 'Expiration Date' },
			],
		},
	],
};
